import { CardBrands, CreditCard } from '@liveauctioneers/caterwaul-components/lib/types/CreditCard';
import { createSelector } from '@reduxjs/toolkit';

import { PaymentProviders } from '@/enums';
import {
    CREDIT_CARD_CACHE_TIME,
    CREDIT_CARD_LOADING_TIME,
    CreditCardSlice,
} from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.types';
import { GlobalState } from '@/redux/store';

const creditCardSelector = (state: GlobalState): CreditCardSlice => state.creditCard;
const passthroughSelector = (_: GlobalState, providerId: PaymentProviders) => providerId;

export const getAllowedToAddCards = createSelector([creditCardSelector], (slice) => slice.allowedToAddCards);
export const getLAPaymentCardsLoaded = createSelector([creditCardSelector], (slice) => slice.loaded);
export const getLAPaymentCardsLoading = createSelector([creditCardSelector], (slice) => slice.loading);
export const getLAPaymentCardsAddSubmitted = createSelector([creditCardSelector], (slice) => slice.addSubmitted);
export const getLAPaymentCardsUpdateSubmitted = createSelector([creditCardSelector], (slice) => slice.updateSubmitted);
export const getCreditCardError = createSelector([creditCardSelector], (slice) => slice.error);

export const creditCardsAnyLoadingSelector = createSelector(
    [getLAPaymentCardsLoading, getLAPaymentCardsAddSubmitted, getLAPaymentCardsUpdateSubmitted],
    (loading, addSubmitted, updateSubmitted) => loading || addSubmitted || updateSubmitted
);

/**
 * returns all cards on file
 * @function getBidderCreditsCards
 * @category Credit Card Service
 * @param {GlobalState}, number
 */
export const getBidderCreditsCards = createSelector([creditCardSelector], (slice) => slice.laPaymentCards);

export const laPaymentCardsForProviderSelector = createSelector(
    [getBidderCreditsCards, passthroughSelector],
    (allLaPaymentCards, providerId) => allLaPaymentCards?.filter((cof) => cof.providerId === providerId)
);

/**
 * returns first matching card on file that is a defaultCard and matches providerId
 * @function getDefaultBidderCreditCard
 * @category Credit Card Service
 * @param {GlobalState}, number
 */
export const getDefaultBidderCreditCard = createSelector(
    [getBidderCreditsCards, passthroughSelector],
    (laPaymentCards, providerId): CreditCard => {
        const defaultCard = laPaymentCards?.find((card) => card.defaultCard && card.providerId === providerId);

        return (
            defaultCard ?? {
                address: {
                    address1: '',
                    city: '',
                    countryCode: '',
                    postalCode: '',
                },
                brand: CardBrands.Other,
                customerId: '',
                id: 0,
                last4: '',
                month: 0,
                name: '',
                providerId: 0,
                token: '',
                year: 0,
            }
        );
    }
);

export const getDefaultBidderCreditCardId = createSelector(getDefaultBidderCreditCard, ({ id }) => id || 0);

//TODO: Should eventually check based on the authed card status.
export const getHasValidCardOnFile = createSelector([getDefaultBidderCreditCard], (laPaymentCard) =>
    Boolean(laPaymentCard?.token)
);

export const shouldFetchBidderCreditCards = createSelector(
    getLAPaymentCardsLoaded,
    getLAPaymentCardsLoading,
    (loaded, loading): boolean => {
        const time = Date.now();
        if (loading) {
            // BUGS-3914 && BUGS-4213 - sometimes loading was stuck as true in the redux state.
            // Changing it to a number and setting a time to retry so we never get stuck without
            // cards loading.
            const diff = time - loaded;
            return diff > CREDIT_CARD_LOADING_TIME;
        }
        const diff = time - loaded;
        return !loaded || diff > CREDIT_CARD_CACHE_TIME;
    }
);
